import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, Tooltip, IconButton, Fab } from '@material-ui/core';
import { BackButton, hasPermission, localization, hasAtLeastOnePermission, getDimensionsOfPagination } from '../../sc-utilities';
import MaterialTable from 'material-table';
import * as actions from './store/operatingSystemActions';
import * as action from '../../np-core/Auth/store/authActions';
import { Refresh, Add, Delete } from '@material-ui/icons';
import OperatingSystemDelete from './OperatingSystemDelete';
import Toolbar from '../../sc-utilities/Toolbar'
import { MuiTableRows } from '../../../themes';
import * as Yup from 'yup';
import Export from '../../sc-components/ExportForm/';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16
    },
    customToolbar: {
        marginTop: 9,
        marginLeft: 14,
        width: "900px"
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];
let initialValues = [
    'LongName', 'Name', 'System'
];
const route = 'operating-systems';

const OperatingSystemList = props => {
    const { classes, translations, deleting, onOperatingSystemType, onOperatingSystemTypeCount, saveColumnsFilter, activeCustomerID, tableColumns, onBulkDeleteOperatingSystemTypes, permissions, redirectHandler, tableRef } = props;
    const columnObject = useMemo(() => tableColumns, [tableColumns]);
    const [deleteModalState, setDeleteModalState] = useState({ open: false, operatingSystemIDs: [] });
    const selectionPermissions = hasAtLeastOnePermission(permissions, ['operating_system_delete', 'system_operating_system_delete']);
    const windowHeight = hasPermission(permissions, 'operating_system_export') ? 330 : 290;

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: selectionPermissions,
        selectionProps: rowData => {
            let disabled = !selectionPermissions;
            rowData.tableData.disabled = disabled
            return disabled ? {
                disabled: true,
                checked: false
            } : {}
        },
        search: false,
        minBodyHeight: window.innerHeight - windowHeight < 300 ? 300 : window.innerHeight - windowHeight,
        maxBodyHeight: window.innerHeight - windowHeight,
        pageSizeOptions: rowsPageSizeInit,

        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    }

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);
        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;
        let count = await onOperatingSystemTypeCount(query);
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count ? await onOperatingSystemType(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{translations["operating_systems"] || 'Operating systems'}</Typography>
                {hasPermission(permissions, 'operating_system_create') ? <Fab
                    variant="extended"
                    size="small"
                    color="primary"
                    aria-label="Add"
                    style={{ borderRadius: '1px' }}
                    className={classes.fab}
                    onClick={() => redirectHandler(`/settings/operating-system/create`)}
                >
                    <Add size="small" />
                    {translations["create_new"] || "Create new"}
                </Fab> : null}
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
            {
                hasPermission(permissions, 'operating_system_export') ?
                    <Export
                        route={route}
                        tableRef={tableRef}
                        initialValues={initialValues}
                        formSettings={formSettings}
                        showTypes={true}
                    /> : null
            }
        </div >
    }

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`OperatingSystem`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID })
        return newColumnState
    }

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, operatingSystemIDs: [] });
    }

    const handleCloseDanger = async () => {
        await onBulkDeleteOperatingSystemTypes({ OperatingSystemIDs: deleteModalState.operatingSystemIDs, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, operatingSystemIDs: [] });
        tableRef.current && tableRef.current.onQueryChange()
    };

    const handleBulkDelete = operatingSystemIDs => {
        setDeleteModalState({ ...deleteModalState, open: true, operatingSystemIDs: operatingSystemIDs });
    };

    // Table Settings
    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["operating_systems"] || 'Operating systems'}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(operatingSystem => operatingSystem.OperatingSystemID))
        }
    ];

    let formSettings = {
        Validation: initialValues.reduce((o, k) => ({ ...o, [k]: Yup.boolean() }), {}),
        ButtonSubmit: hasPermission(permissions, "operating_system_export") ? {
            Title: translations["download"] || "Download",
            Color: 'primary'
        } : null
    };

    return (
        <Paper>
            <MaterialTable
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {hasPermission(permissions, 'operating_system_delete') ? (
                <OperatingSystemDelete
                    activeCustomerID={activeCustomerID}
                    entityName={translations["operating_systems"] || 'Operating systems'}
                    translations={translations}
                    deleting={deleting}
                    handleCloseDanger={handleCloseDanger}
                    handleCloseSafe={handleCloseSafe}
                    deleteModalState={deleteModalState}
                />
            ) : null}
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID,
        translations: state?.translate?.translations?.activeTranslations || {},
        deleting: state?.setting?.operatingSystems?.deleting,
        permissions: state?.core?.login?.permissions || []
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onOperatingSystemTypeCount: filter => dispatch(actions.fetchOperatingSystemCount(filter)),
        onOperatingSystemType: filter => dispatch(actions.fetchOperatingSystem(filter)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        onBulkDeleteOperatingSystemTypes: data => dispatch(actions.bulkDeleteOperatingSystems(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(OperatingSystemList));


